import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useI18n } from '../lib/i18n/I18nProvider';

export function Card({
  title,
  description,
  actions,
  children,
  testId,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  /**
   * A stable handle for a card whose contents an end-to-end test has to scope to.
   *
   * Screens carry several cards with the same controls — two approval hand-offs on the expo
   * detail, a "Save" on the recording policy and another on the SLA — and a suite that reached them
   * by heading text was one translation away from breaking, in a repository where every screen is
   * bilingual. It is optional: only the cards a spec actually drives carry one.
   */
  testId?: string;
}): ReactElement {
  return (
    <section className="card" {...(testId ? { 'data-testid': testId } : {})}>
      {title || actions ? (
        <header>
          <div>
            {title ? <h2>{title}</h2> : null}
            {description ? <p className="muted">{description}</p> : null}
          </div>
          {actions ? <div className="spacer">{actions}</div> : null}
        </header>
      ) : null}
      {children}
    </section>
  );
}

export function PageTitle({ title, children }: { title: string; children?: ReactNode }): ReactElement {
  return (
    <div className="page-title">
      <h1>{title}</h1>
      {children ? <p>{children}</p> : null}
    </div>
  );
}

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'ok' | 'warn' | 'danger';
  children: ReactNode;
}): ReactElement {
  const cls = tone === 'neutral' ? 'badge' : `badge badge--${tone}`;
  return <span className={cls}>{children}</span>;
}

/** An identifier, hash or reference number: always `ltr`, even inside a mirrored table. */
export function Ltr({ children }: { children: ReactNode }): ReactElement {
  return (
    <span className="ltr" dir="ltr">
      {children}
    </span>
  );
}

/** Money renders in minor units, formatted for the locale, ALWAYS with its ISO 4217 code. */
export function Money({ minorUnits, currency }: { minorUnits: number; currency: string }): ReactElement {
  const { formatNumber } = useI18n();
  return (
    <span className="numeral">
      {formatNumber(minorUnits)} <Ltr>{currency}</Ltr>
    </span>
  );
}

export interface FieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (next: string) => void;
  hint?: string;
  error?: string | undefined;
  type?: 'text' | 'password' | 'email' | 'number' | 'datetime-local' | 'tel' | 'url';
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
  /** Identifiers and codes read left-to-right whatever the page direction. */
  ltr?: boolean;
}

export function Field({
  label,
  name,
  value,
  onChange,
  hint,
  error,
  type = 'text',
  required,
  placeholder,
  autoComplete,
  ltr,
}: FieldProps): ReactElement {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');
  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        value={value}
        required={required}
        placeholder={placeholder ?? ''}
        autoComplete={autoComplete ?? 'off'}
        dir={ltr ? 'ltr' : undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      />
      {hint ? (
        <span className="hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="field-error" id={errorId}>
          {error}
        </span>
      ) : null}
    </div>
  );
}

export function TextArea({
  label,
  name,
  value,
  onChange,
  hint,
  error,
  required,
  readOnly,
  ltr,
}: Omit<FieldProps, 'type' | 'placeholder' | 'autoComplete'> & { readOnly?: boolean }): ReactElement {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  // The hint and the error are ASSOCIATED, not merely adjacent: a screen reader that reaches the
  // control has to hear why it was refused, and `aria-describedby` is the only thing that says so.
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');
  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </label>
      <textarea
        id={id}
        name={name}
        value={value}
        required={required}
        readOnly={readOnly}
        dir={ltr ? 'ltr' : undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint ? (
        <span className="hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="field-error" id={errorId}>
          {error}
        </span>
      ) : null}
    </div>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

export function Select({
  label,
  name,
  value,
  options,
  onChange,
  hint,
  error,
  required,
}: {
  label: string;
  name: string;
  value: string;
  options: SelectOption[];
  onChange: (next: string) => void;
  hint?: string;
  error?: string | undefined;
  required?: boolean;
}): ReactElement {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');
  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </label>
      <select
        id={id}
        name={name}
        value={value}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {hint ? (
        <span className="hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="field-error" id={errorId}>
          {error}
        </span>
      ) : null}
    </div>
  );
}

/**
 * A dense operational table. The column order mirrors with the page direction (the browser does
 * that for a `<table>` in an RTL document); identifiers inside cells stay `ltr` through <Ltr>.
 */
export function DataTable<T>({
  caption,
  columns,
  rows,
  rowKey,
  empty,
}: {
  caption: string;
  columns: { key: string; header: string; cell: (row: T) => ReactNode }[];
  rows: T[];
  rowKey: (row: T) => string;
  empty?: ReactNode;
}): ReactElement {
  if (rows.length === 0 && empty) return <>{empty}</>;
  return (
    <div className="table-scroll">
      <table className="data">
        <caption className="visually-hidden">{caption}</caption>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} scope="col">
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)}>
              {columns.map((c) => (
                <td key={c.key}>{c.cell(row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A modal built on <dialog>, so focus trapping and Escape come from the platform. */
export function Modal({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}): ReactElement | null {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  const handleCancel = useCallback(
    (e: React.SyntheticEvent<HTMLDialogElement>) => {
      e.preventDefault();
      onClose();
    },
    [onClose],
  );

  if (!open) return null;
  return (
    <dialog className="modal" ref={ref} aria-labelledby={titleId} onCancel={handleCancel} onClose={onClose}>
      <h2 id={titleId}>{title}</h2>
      {children}
    </dialog>
  );
}

/**
 * The typed-confirmation control for terminal operations (expo cancellation, booth takedown,
 * recording deletion, account deletion). The operator types the object's identifier.
 */
export function TypedConfirm({
  expected,
  onConfirmedChange,
}: {
  expected: string;
  onConfirmedChange: (confirmed: boolean) => void;
}): ReactElement {
  const { t } = useI18n();
  const [typed, setTyped] = useState('');
  const matches = typed.trim() === expected;

  useEffect(() => {
    onConfirmedChange(matches);
  }, [matches, onConfirmedChange]);

  return (
    <Field
      label={t('guard.typed.label', { value: expected })}
      name="typed-confirmation"
      value={typed}
      onChange={setTyped}
      ltr
      {...(typed && !matches ? { error: t('guard.typed.mismatch') } : {})}
    />
  );
}
